import { useCallback, useState } from 'react';
import { NOTES_SAVED_ERROR, NOTES_SAVED_SUCCESS, NOTES_SHOULD_NOT_BE_EMPTY } from 'common/constants/services/notes';
import { NoteData, OnAddNoteParams } from 'components/Notes/types';
import { getArrayValue } from 'utilities';
import { useResponse } from '../useResponse';
import { useSaveNotes } from './useSaveNotes';
import { onAddNote, onDeleteNote, onUpdateNotes } from './utilities';

interface SaveNotesOptions {
  onSuccess?: (savedNotes: NoteData[]) => void;
  onError?: () => void;
  onSettled?: () => void;
}

export const useNotes = () => {
  const [notes, setNotes] = useState<NoteData[] | null>(null);
  const [notesHasChanged, setNotesHasChanged] = useState<boolean>(false);
  const { mutate: saveNotesFetch } = useSaveNotes();
  const { infoNotification, errorNotification, successNotification } = useResponse();

  const getParsedNotes = useCallback(
    (notesData: NoteData[]) => {
      const notesToSave = notesData ?? notes;
      if (notesToSave && notesToSave?.length > 0) {
        return notesToSave.map(({ temp_id, isNew, author, updated_by, ...rest }) => rest);
      }
      return [];
    },
    [notes]
  );

  const isAnyNoteEmpty = (notesData: NoteData[]) => !notesData.some(note => note.content === '');

  const saveNotes = useCallback(
    (notesData?: NoteData[], options?: SaveNotesOptions) => {
      const notesToSave = notesData ?? notes;
      if (!notesToSave) {
        return;
      }

      const isAllNotesValidContent = isAnyNoteEmpty(getArrayValue(notesToSave));
      if (!isAllNotesValidContent) {
        infoNotification(NOTES_SHOULD_NOT_BE_EMPTY);
        return;
      }

      const parsedNotes = getParsedNotes(notesToSave);
      saveNotesFetch(
        { notes: parsedNotes },
        {
          // Keep existing notifications/state updates and expose optional hooks
          // for callers that need to synchronize local state changes.
          onSuccess: savedNotes => {
            successNotification(NOTES_SAVED_SUCCESS);
            setNotesHasChanged(false);
            options?.onSuccess?.(savedNotes);
          },
          onError: () => {
            errorNotification(NOTES_SAVED_ERROR);
            options?.onError?.();
          },
          onSettled: () => {
            options?.onSettled?.();
          },
        }
      );
    },
    [notes, getParsedNotes, saveNotesFetch, successNotification, errorNotification, infoNotification]
  );

  const handleOnAddNote = useCallback(
    ({ pageType, pageTypeId }: OnAddNoteParams) => {
      onAddNote({
        notes: notes ?? [],
        setNotes,
        pageType,
        pageTypeId,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  const handleOnUpdateNotes = useCallback(
    (updatedNote: NoteData) => {
      onUpdateNotes({
        notes: notes ?? [],
        setNotes,
        updatedNote,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  const handleOnDeleteNote = useCallback(
    (updatedNote: NoteData) => {
      onDeleteNote({
        notes: notes ?? [],
        setNotes,
        updatedNote,
      });
      setNotesHasChanged(true);
    },
    [notes, setNotes]
  );

  return {
    notes,
    setNotes,
    notesHasChanged,
    setNotesHasChanged,
    saveNotes,
    onAddNote: handleOnAddNote,
    onUpdateNotes: handleOnUpdateNotes,
    onDeleteNote: handleOnDeleteNote,
  };
};

export default useNotes;
